import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFlow } from "@/contexts/FlowContext";
import {
  Mail, KeyRound, ArrowRight, Shield, Loader2, CheckCircle2,
  RefreshCw, Unlink, Clock, ShieldCheck, AlertTriangle, XCircle
} from "lucide-react";
import type { ProviderConnectionStatus } from "@/contexts/FlowContext";

const statusConfig: Record<ProviderConnectionStatus, { label: string; icon: typeof CheckCircle2; color: string; bg: string }> = {
  connected: { label: "Connected", icon: CheckCircle2, color: "text-trust-green", bg: "bg-trust-green-muted" },
  reconnect_required: { label: "Reconnect required", icon: AlertTriangle, color: "text-trust-amber-foreground", bg: "bg-trust-amber-muted" },
  not_completed: { label: "Not completed", icon: XCircle, color: "text-destructive", bg: "bg-destructive/10" },
};

export default function ResumeGate() {
  const { state, startResumeVerification, completeResumeVerification, cancelResume, reconnectProvider, disconnectProvider } = useFlow();
  const [method, setMethod] = useState<"magic_link" | "otp" | null>(null);
  const [email, setEmail] = useState(state.applicantEmail);
  const [otp, setOtp] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const timeRemaining = state.draftExpiresAt
    ? Math.max(0, Math.round((new Date(state.draftExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60)))
    : 0;

  // Restoring state
  if (state.resumePhase === "restoring") {
    return (
      <div className="max-w-md mx-auto mt-16">
        <Card className="bg-card shadow-sm">
          <CardContent className="p-8 text-center space-y-4">
            <Loader2 className="w-10 h-10 text-primary mx-auto animate-spin" />
            <h2 className="text-lg font-display font-semibold text-foreground">Restoring your application</h2>
            <p className="text-sm text-muted-foreground">
              Retrieving your saved progress and connected accounts…
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Resumed — show connected providers summary before continuing
  if (state.resumePhase === "resumed") {
    return (
      <div className="max-w-lg mx-auto mt-8 space-y-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-card shadow-sm">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-trust-green-muted flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-trust-green" />
                </div>
                <div>
                  <h2 className="font-display font-semibold text-foreground">Welcome back, Juan</h2>
                  <p className="text-xs text-muted-foreground">Your application has been restored. Continue where you left off.</p>
                </div>
              </div>

              {/* Draft info */}
              <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                <span>Draft saved — {timeRemaining} hours remaining to complete</span>
              </div>

              {/* Connected providers */}
              {state.connectedProviders.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Connected accounts</p>
                  {state.connectedProviders.map((provider) => {
                    const config = statusConfig[provider.status];
                    const StatusIcon = config.icon;
                    return (
                      <div key={provider.provider} className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center`}>
                            <StatusIcon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{provider.provider} account connected</p>
                            <p className="text-xs text-muted-foreground">
                              {provider.status === "reconnect_required"
                                ? `Previously connected as ${provider.maskedEmail}. Reconnect required.`
                                : provider.status === "not_completed"
                                ? `${provider.provider} connection was not completed. Please reconnect to continue.`
                                : `Connected as ${provider.maskedEmail}`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {provider.status === "reconnect_required" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs gap-1"
                              onClick={() => reconnectProvider(provider.provider)}
                            >
                              <RefreshCw className="w-3 h-3" /> Reconnect
                            </Button>
                          )}
                          {provider.status === "not_completed" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs gap-1"
                              onClick={() => reconnectProvider(provider.provider)}
                            >
                              <RefreshCw className="w-3 h-3" /> Reconnect
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-muted-foreground gap-1"
                            onClick={() => disconnectProvider(provider.provider)}
                          >
                            <Unlink className="w-3 h-3" /> Disconnect
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-trust-green" />
                <span>Your saved information is encrypted and will be automatically deleted after the 76-hour window expires.</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Verification gate
  return (
    <div className="max-w-md mx-auto mt-16 space-y-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 bg-trust-blue-muted text-trust-blue px-3 py-1 rounded-full text-xs font-medium">
          <Shield className="w-3.5 h-3.5" /> Continue your application
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground">Welcome back</h1>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          We found a saved draft for your application. Verify your identity to pick up where you left off.
        </p>
        <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>{timeRemaining} hours remaining</span>
        </div>
      </motion.div>

      <Card className="bg-card shadow-sm">
        <CardContent className="p-6 space-y-4">
          {!method ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">Choose verification method</p>
              <button
                onClick={() => setMethod("magic_link")}
                className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-trust-blue-muted flex items-center justify-center">
                  <Mail className="w-5 h-5 text-trust-blue" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Magic link</p>
                  <p className="text-xs text-muted-foreground">We'll send a one-time link to your email</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />
              </button>
              <button
                onClick={() => setMethod("otp")}
                className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-trust-blue-muted flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-trust-blue" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Email code</p>
                  <p className="text-xs text-muted-foreground">We'll send a 6-digit code to your email</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />
              </button>
            </div>
          ) : method === "magic_link" ? (
            <AnimatePresence mode="wait">
              {!magicLinkSent ? (
                <motion.div key="send" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-trust-blue" />
                    <p className="text-sm font-medium text-foreground">Send magic link</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Email address</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This link can only be used once and will expire in 15 minutes.
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setMethod(null)}>Back</Button>
                    <Button className="flex-1" onClick={() => { startResumeVerification(); setMagicLinkSent(true); }}>
                      Send link <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="sent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-trust-green-muted mx-auto flex items-center justify-center">
                    <Mail className="w-6 h-6 text-trust-green" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Check your email</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      We sent a one-time link to <span className="font-medium text-foreground">{email}</span>
                    </p>
                  </div>
                  <Button className="w-full" onClick={completeResumeVerification}>
                    Simulate: Click magic link
                  </Button>
                  <button onClick={() => { setMagicLinkSent(false); setMethod(null); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Try a different method
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-trust-blue" />
                <p className="text-sm font-medium text-foreground">Enter verification code</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Email address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">6-digit code</label>
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-lg tracking-[0.5em] font-mono"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the 6-digit code sent to your email. Code expires in 10 minutes.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setMethod(null); setOtp(""); }}>Back</Button>
                <Button
                  className="flex-1"
                  onClick={() => { startResumeVerification(); completeResumeVerification(); }}
                  disabled={otp.length < 6}
                >
                  Verify <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      <div className="text-center">
        <button onClick={cancelResume} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
          Start a new application instead
        </button>
      </div>
    </div>
  );
}
